import { useEffect, useRef, useState } from "react";

const PROGRESS_DELAY_MS = 2000;
const PROGRESS_INTERVAL_MS = 700;
const COMPLETE_HIDE_DELAY_MS = 350;
const PROGRESS_START_VALUE = 20;
const PROGRESS_MAX_WAIT_VALUE = 90;

const getNextProgressValue = (currentProgress) => {
    if (currentProgress >= PROGRESS_MAX_WAIT_VALUE) {
        return PROGRESS_MAX_WAIT_VALUE;
    }

    const remainingProgress = PROGRESS_MAX_WAIT_VALUE - currentProgress;
    const nextStep = Math.max(1, Math.ceil(remainingProgress * 0.22));

    return Math.min(PROGRESS_MAX_WAIT_VALUE, currentProgress + nextStep);
};

export default function useDelayedProgressLoader(isLoading) {
    const [isLoaderVisible, setIsLoaderVisible] = useState(isLoading);
    const [isProgressMode, setIsProgressMode] = useState(false);
    const [progress, setProgress] = useState(0);
    const hasProgressModeStartedRef = useRef(false);
    const timersRef = useRef({
        delayTimer: null,
        progressTimer: null,
        completeTimer: null,
    });

    useEffect(() => {
        const timers = timersRef.current;

        return () => {
            clearTimeout(timers.delayTimer);
            clearInterval(timers.progressTimer);
            clearTimeout(timers.completeTimer);
        };
    }, []);

    useEffect(() => {
        const timers = timersRef.current;

        clearTimeout(timers.delayTimer);
        clearInterval(timers.progressTimer);
        clearTimeout(timers.completeTimer);

        if (isLoading) {
            hasProgressModeStartedRef.current = false;
            setIsLoaderVisible(true);
            setIsProgressMode(false);
            setProgress(0);

            timers.delayTimer = setTimeout(() => {
                hasProgressModeStartedRef.current = true;
                setIsProgressMode(true);
                setProgress(PROGRESS_START_VALUE);

                timers.progressTimer = setInterval(() => {
                    setProgress((currentProgress) =>
                        getNextProgressValue(currentProgress),
                    );
                }, PROGRESS_INTERVAL_MS);
            }, PROGRESS_DELAY_MS);

            return undefined;
        }

        if (hasProgressModeStartedRef.current) {
            setProgress(100);
            timers.completeTimer = setTimeout(() => {
                setIsLoaderVisible(false);
                setIsProgressMode(false);
                setProgress(0);
                hasProgressModeStartedRef.current = false;
            }, COMPLETE_HIDE_DELAY_MS);

            return undefined;
        }

        setIsLoaderVisible(false);
        setIsProgressMode(false);
        setProgress(0);

        return undefined;
    }, [isLoading]);

    return {
        isLoaderVisible,
        isProgressMode,
        progress,
    };
}
